import { useEffect, useState, useCallback, useRef } from 'react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client/dist/sockjs';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import notificationService from '../services/notificationService';
import { NOTIFICATION_TYPES, isNotificationExpired } from '../types/notifications';

const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_DELAY = 5000;

/**
 * Custom hook for managing notifications
 * Handles WebSocket connection, API calls, and notification state
 * 
 * @param {string} jwt - JWT token for authentication (optional, uses cookie if not provided)
 * @returns {Object} Notification state and actions
 */
export const useNotifications = (jwt) => {
  // Use Redux directly instead of useAuth to avoid Router dependency
  const { user } = useSelector((state) => state.auth);
  const isAuthenticated = !!user; // Derive authentication status from user existence
  
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  
  const stompClientRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const retryTimeoutRef = useRef(null);
  
  const endpoint = process.env.REACT_APP_WEBSOCKET_ENDPOINT || 'http://localhost:8080/ws';

  /**
   * Fetch notifications from API
   */
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      setIsLoading(true);
      setError(null);
      const fetchedNotifications = await notificationService.getUnreadNotifications();
      
      // Filter out expired notifications
      const validNotifications = fetchedNotifications.filter(notification => 
        !isNotificationExpired(notification)
      );
      
      setNotifications(validNotifications);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  /**
   * Mark a notification as read
   */
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await notificationService.markNotificationAsRead(notificationId);
      
      // Remove notification from local state
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      
      toast.success('Notification dismissed');
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      toast.error('Failed to dismiss notification');
    }
  }, []);

  /**
   * Delete a video (for over-limit notifications)
   */
  const deleteVideo = useCallback(async (videoId) => {
    try {
      await notificationService.deleteMyVideo(videoId);
      toast.success('Video deleted successfully');
      return true;
    } catch (err) {
      console.error('Failed to delete video:', err);
      toast.error('Failed to delete video');
      return false;
    }
  }, []);

  /**
   * Show appropriate toast notification based on notification type
   * @param {Object} notification - Notification payload
   */
  const showNotificationToast = useCallback((notification) => {
    const { type, title, body, actionUrl } = notification;
    
    switch (type) {
      case NOTIFICATION_TYPES.OVER_LIMIT_PROMPT:
        toast.warning(title, {
          autoClose: 15000,

          onClick: () => {
            if (actionUrl) {
              window.location.href = actionUrl;
            }
          },
          style: {
            cursor: actionUrl ? 'pointer' : 'default'
          }
        });
        break;
        
      case NOTIFICATION_TYPES.LIVESTREAM_STARTED:
        toast.info(body || title, {
          autoClose: 8000,

          onClick: () => {
            if (actionUrl) {
              window.location.href = actionUrl;
            }
          },
          style: {
            cursor: actionUrl ? 'pointer' : 'default'
          }
        });
        break;
        
      case NOTIFICATION_TYPES.PAYMENT_SUCCESS:
        toast.success(title, {
          autoClose: 5000,
          pauseOnHover: true
        });
        break;
        
      case NOTIFICATION_TYPES.PAYMENT_FAILED:
        toast.error(title, {
          autoClose: 8000,
          pauseOnHover: true
        });
        break;
        
      case NOTIFICATION_TYPES.SUBSCRIPTION_EXPIRED:
        toast.warning(title, {
          autoClose: 10000,

          onClick: () => {
            if (actionUrl) {
              window.location.href = actionUrl;
            }
          },
          style: {
            cursor: actionUrl ? 'pointer' : 'default'
          }
        });
        break;
        
      case NOTIFICATION_TYPES.QUOTA_WARNING:
        toast.warning(title, {
          autoClose: 8000,
          pauseOnHover: true
        });
        break;
        
      case NOTIFICATION_TYPES.QUOTA_EXCEEDED:
        toast.error(title, {
          autoClose: 10000,

          onClick: () => {
            if (actionUrl) {
              window.location.href = actionUrl;
            }
          },
          style: {
            cursor: actionUrl ? 'pointer' : 'default'
          }
        });
        break;
        
      default:
        // Generic notification
        toast.info(title, {
          autoClose: 5000,

          onClick: () => {
            if (actionUrl) {
              window.location.href = actionUrl;
            }
          },
          style: {
            cursor: actionUrl ? 'pointer' : 'default'
          }
        });
        break;
    }
  }, []);

  /**
   * Connect to WebSocket for real-time notifications
   */
  const connectWebSocket = useCallback(() => {
    if (!isAuthenticated || !user?.username) {
      return;
    }

    // Don't reconnect if we already have an active connection
    if (stompClientRef.current?.connected) {
      return;
    }

    // Clean up existing connection only if it's not connected
    if (stompClientRef.current && !stompClientRef.current.connected) {
      stompClientRef.current.deactivate();
    }
    const socket = new SockJS(endpoint, null, {
      // Include cookies for authentication (same as API calls)
      // Include JWT token in headers if provided (fallback)
      ...(jwt && {
        headers: {
          'Authorization': `Bearer ${jwt}`
        }
      })
    });
    const stompClient = new Client({
      webSocketFactory: () => socket,
      debug: (str) => {
        // Disable debug logging in production
        if (process.env.NODE_ENV === 'development') {
          console.log('STOMP Debug:', str);
        }
      },

      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      onConnect: () => {
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
        setError(null);

        // Subscribe to user-specific notification queue
        const subscription = stompClient.subscribe(`/user/queue/notifications`, (message) => {
          try {
            const notification = JSON.parse(message.body);
            
            // Add new notification to state
            setNotifications(prev => {
              // Avoid duplicates
              const exists = prev.some(n => n.id === notification.id);
              if (exists) return prev;
              
              return [notification, ...prev];
            });

            // Show toast for important notifications
            showNotificationToast(notification);
          } catch (err) {
            console.error('Error parsing notification message:', err);
          }
        });

        // Store subscription reference for cleanup
        stompClientRef.current.subscription = subscription;
      },
      onStompError: (frame) => {
        console.error('❌ STOMP error:', frame);
        setIsConnected(false);
        setError('WebSocket connection error. Notifications will be fetched via API.');
        
        // Attempt reconnection
        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttemptsRef.current++;
          retryTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, RECONNECT_DELAY);
        } else {
          setError('Failed to connect to notifications service. Using API fallback.');
        }
      },
      onWebSocketClose: () => {
        setIsConnected(false);
      }
    });

    stompClientRef.current = stompClient;
    stompClient.activate();
  }, [isAuthenticated, user?.username, jwt, endpoint, showNotificationToast]);

  /**
   * Disconnect WebSocket
   */
  const disconnectWebSocket = useCallback(() => {
    if (stompClientRef.current) {
      stompClientRef.current.deactivate();
      stompClientRef.current = null;
    }
    
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    
    setIsConnected(false);
  }, []);

  // Initialize notifications and WebSocket connection
  // Connect to WebSocket and fetch notifications when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications();
      // Only connect if not already connected
      if (!stompClientRef.current?.connected) {
        connectWebSocket();
      }
    } else {
      setNotifications([]);
      disconnectWebSocket();
    }

    return () => {
      // Don't disconnect on every re-render, only on unmount
    };
  }, [isAuthenticated]); // Only depend on isAuthenticated to prevent infinite loops

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (stompClientRef.current) {
        stompClientRef.current.deactivate();
        stompClientRef.current = null;
      }
      
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, []); // Empty dependency array for cleanup only

  // Get specific notification types
  const getOverLimitNotifications = useCallback(() => {
    return notifications.filter(n => n.type === NOTIFICATION_TYPES.OVER_LIMIT_PROMPT);
  }, [notifications]);

  // Get unread count
  const unreadCount = notifications.length;

  return {
    notifications,
    isLoading,
    isConnected,
    error,
    unreadCount,
    markAsRead,
    deleteVideo,
    fetchNotifications,
    getOverLimitNotifications,
    // Legacy compatibility

    markRead: markAsRead
  };
};

export default useNotifications;
